import React from "react";
import { X, Upload, CheckCircle2, AlertCircle } from "lucide-react";

export interface UploadStatus {
  fileName: string;
  progress: number; // 0-100
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

interface UploadStatusBarProps {
  uploadStatus: UploadStatus | null;
  onDismiss?: () => void;
}

const UploadStatusBar: React.FC<UploadStatusBarProps> = ({ uploadStatus, onDismiss }) => {
  if (!uploadStatus) return null;

  const getStatusColor = () => {
    switch (uploadStatus.status) {
      case 'uploading':
        return 'bg-blue-500';
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusIcon = () => {
    switch (uploadStatus.status) {
      case 'uploading':
        return <Upload className="w-4 h-4 animate-pulse" />;
      case 'success':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'error':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (uploadStatus.status) {
      case 'uploading':
        return `Uploading ${uploadStatus.fileName}... ${Math.round(uploadStatus.progress)}%`;
      case 'success':
        return `Successfully uploaded ${uploadStatus.fileName}`;
      case 'error':
        return `Failed to upload ${uploadStatus.fileName}${uploadStatus.error ? `: ${uploadStatus.error}` : ''}`;
      default:
        return '';
    }
  };

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-[9999] ${getStatusColor()} text-white shadow-lg`}>
      <div className="container mx-auto px-4 py-3 z-[99999]">
        {uploadStatus.status === 'uploading' && (
          <div className="flex items-center gap-2 text-sm font-bold mb-2">
            <AlertCircle className="w-6 h-6 text-yellow-500" />
            <span className="text-sm font-medium">
              Do not navigate away from this page until upload is complete
            </span>
          </div>
        )}
        <div className="flex items-center justify-between z-[99999]">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {getStatusIcon()}
            <span className="font-medium truncate">
              {getStatusText()}
            </span>
            {uploadStatus.status === 'uploading' && (
              <div className="flex-1 max-w-lg ml-4">
                <div className="w-full bg-white/20 rounded-full h-1.5">
                  <div
                    className="bg-white h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadStatus.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          {(uploadStatus.status === 'success' || uploadStatus.status === 'error') && onDismiss && (
            <button
              onClick={onDismiss}
              className="ml-4 p-1 hover:bg-white/20 rounded transition-colors z-[99999]"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadStatusBar;
